import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarDays,
  ListChecks,
  Sparkles,
} from "lucide-react-native";
import {
  Image,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  Dialog,
  DialogActions,
  DialogPrimaryButton,
  MoonDateField,
  MoonFieldInput,
  MoonFormField,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import { useCanonicalCycleIntake } from "@src/packages/care/clinical-record";
import {
  CYCLE_TYPE_LABEL_KEYS,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const CYCLE_TYPES: CycleType[] = [
  "ivf",
  "icsi",
  "iui",
  "fet",
  "oi",
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
];

type PastCycleOutcome =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other";

const OUTCOME_OPTIONS: {
  value: PastCycleOutcome;
  labelKey: string;
}[] = [
  {
    value: "pregnancy",
    labelKey: "you.modals.end_cycle.outcome_pregnancy",
  },
  {
    value: "no_pregnancy",
    labelKey: "you.modals.end_cycle.outcome_no_pregnancy",
  },
  {
    value: "miscarriage",
    labelKey: "you.modals.end_cycle.outcome_miscarriage",
  },
  {
    value: "cancelled",
    labelKey: "you.modals.end_cycle.outcome_cancelled",
  },
  {
    value: "other",
    labelKey: "you.modals.end_cycle.outcome_other",
  },
];

export default function AddPastCycleDialog({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("AddPastCycleDialog");
  const { save: saveCanonicalCycle, isPending: isSavingCanonicalCycle } =
    useCanonicalCycleIntake();
  const [cycleType, setCycleType] = useState<CycleType | null>(null);
  const [clinic, setClinic] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [outcome, setOutcome] = useState<PastCycleOutcome | null>(null);
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const isPreservationCycle =
    cycleType === "egg_freezing" ||
    cycleType === "embryo_freezing" ||
    cycleType === "sperm_freezing";

  const cycleTypeOptions = useMemo(
    () =>
      CYCLE_TYPES.map((value) => ({
        value,
        label: t(CYCLE_TYPE_LABEL_KEYS[value] ?? value),
      })),
    [t],
  );

  useEffect(() => {
    if (!visible) return;
    setCycleType(null);
    setClinic("");
    setStartDate("");
    setEndDate("");
    setOutcome(null);
    setNotes("");
    setFormError(null);
  }, [visible]);

  const handleClose = () => {
    if (isSavingCanonicalCycle) return;
    onClose();
  };

  const handleSave = async () => {
    setFormError(null);
    if (!cycleType || !startDate) {
      setFormError(t("care.all_past_cycles.validation_required"));
      return;
    }
    if (endDate && endDate < startDate) {
      setFormError(t("care.all_past_cycles.validation_dates"));
      return;
    }

    const clinicName = clinic.trim() || null;

    try {
      await saveCanonicalCycle({
        cycleType,
        status: "closed",
        description: notes.trim() || null,
        startDate,
        endDate: endDate || null,
        provider: clinicName,
        clinic: clinicName,
        ...(outcome && !isPreservationCycle
          ? {
              outcome: {
                type: outcome,
                dateRecorded: endDate || startDate,
                notes: notes.trim() || null,
              },
            }
          : {}),
      });
      onClose();
    } catch {
      setFormError(t("care.all_past_cycles.save_error"));
    }
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
      dismissOnBackdropTap={!isSavingCanonicalCycle}
      containerStyle={{ width: "92%", maxWidth: 460 }}
    >
      <Image
        source={require("../../../../assets/images/past-cycle-dialog-illustration.png")}
        style={{
          alignSelf: "center",
          width: 78,
          height: 78,
        }}
        resizeMode="contain"
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
          marginTop: 12,
        }}
      >
        {t("care.all_past_cycles.add_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 18,
          color: EMBER.colors.textLight,
          textAlign: "center",
          marginTop: 4,
          marginBottom: 18,
        }}
      >
        {t("care.all_past_cycles.add_subtitle")}
      </Text>

      <ScrollView
        style={{ maxHeight: 440 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <MoonFormField
          label={t("care.all_past_cycles.type_label")}
          labelIcon={ListChecks}
        >
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            {cycleTypeOptions.map((option) => {
              const selected = cycleType === option.value;
              return (
                <Pressable
                  key={option.value}
                  accessibilityRole="button"
                  accessibilityLabel={option.label}
                  accessibilityState={{ selected }}
                  onPress={() => {
                    capturePress(pressLabel(option.label), "cycle_type_option");
                    setCycleType(option.value);
                  }}
                  style={({ pressed }) => ({
                    width: "48%",
                    minHeight: 42,
                    justifyContent: "center",
                    paddingHorizontal: 10,
                    paddingVertical: 8,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: selected
                      ? "rgba(139,58,26,0.32)"
                      : "rgba(42,37,34,0.1)",
                    backgroundColor: selected
                      ? "rgba(139,58,26,0.08)"
                      : pressed
                        ? "rgba(42,37,34,0.04)"
                        : "rgba(255,255,255,0.34)",
                  })}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: selected ? "700" : "500",
                      color: selected
                        ? EMBER.colors.warm
                        : EMBER.colors.textMid,
                      textAlign: "center",
                    }}
                  >
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </MoonFormField>

        <View style={{ marginTop: 16 }}>
          <MoonFormField
            label={t("care.all_past_cycles.clinic_label")}
            labelIcon={Building2}
          >
            <MoonFieldInput>
              <TextInput
                value={clinic}
                onChangeText={setClinic}
                placeholder={t("care.all_past_cycles.clinic_placeholder")}
                placeholderTextColor={EMBER.colors.textFaint}
                style={{
                  paddingVertical: 10,
                  fontSize: 14,
                  color: EMBER.colors.text,
                  fontFamily: "DMSans_400Regular",
                }}
              />
            </MoonFieldInput>
          </MoonFormField>
        </View>

        <View style={{ flexDirection: "row", gap: 10, marginTop: 16 }}>
          <View style={{ flex: 1 }}>
            <MoonDateField
              label={t("care.all_past_cycles.start_date_label")}
              labelIcon={CalendarDays}
              value={startDate}
              onChange={setStartDate}
              placeholder={t("care.all_past_cycles.date_placeholder")}
              required
            />
          </View>
          <View style={{ flex: 1 }}>
            <MoonDateField
              label={t("care.all_past_cycles.end_date_label")}
              labelIcon={CalendarDays}
              value={endDate}
              onChange={setEndDate}
              placeholder={t("care.all_past_cycles.date_placeholder")}
            />
          </View>
        </View>

        <View style={{ marginTop: 16 }}>
          {isPreservationCycle ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textLight,
              }}
            >
              {t("care.all_past_cycles.preservation_outcome_unavailable")}
            </Text>
          ) : (
            <MoonFormField
              label={t("care.all_past_cycles.outcome_label")}
              labelIcon={Sparkles}
            >
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {OUTCOME_OPTIONS.map((option) => {
                  const selected = outcome === option.value;
                  return (
                    <Pressable
                      key={option.value}
                      accessibilityRole="button"
                      accessibilityLabel={t(option.labelKey)}
                      accessibilityState={{ selected }}
                      onPress={() => {
                        capturePress(
                          pressLabel(t(option.labelKey)),
                          "cycle_outcome_option",
                        );
                        setOutcome(selected ? null : option.value);
                      }}
                      style={({ pressed }) => ({
                        width: "48%",
                        paddingHorizontal: 10,
                        paddingVertical: 9,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: selected
                          ? "rgba(139,58,26,0.32)"
                          : "rgba(42,37,34,0.1)",
                        backgroundColor: selected
                          ? "rgba(139,58,26,0.08)"
                          : pressed
                            ? "rgba(42,37,34,0.04)"
                            : "rgba(255,255,255,0.34)",
                      })}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          fontWeight: selected ? "700" : "500",
                          color: selected
                            ? EMBER.colors.warm
                            : EMBER.colors.textMid,
                          textAlign: "center",
                        }}
                      >
                        {t(option.labelKey)}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </MoonFormField>
          )}
        </View>

        <View style={{ marginTop: 16 }}>
          <MoonFormField label={t("care.all_past_cycles.notes_label")}>
            <MoonFieldInput>
              <TextInput
                value={notes}
                onChangeText={setNotes}
                placeholder={t("care.all_past_cycles.notes_placeholder")}
                placeholderTextColor={EMBER.colors.textFaint}
                multiline
                numberOfLines={3}
                style={{
                  minHeight: 72,
                  paddingVertical: 10,
                  fontSize: 14,
                  color: EMBER.colors.text,
                  fontFamily: "DMSans_400Regular",
                  textAlignVertical: "top",
                }}
              />
            </MoonFieldInput>
          </MoonFormField>
        </View>

        {formError ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.coral,
              marginTop: 12,
            }}
          >
            {formError}
          </Text>
        ) : null}
      </ScrollView>

      <DialogActions style={{ marginTop: 18 }}>
        <SoftSecondaryButton
          label={t("care.all_past_cycles.cancel_button")}
          onPress={handleClose}
          disabled={isSavingCanonicalCycle}
          flex={1}
        />
        <DialogPrimaryButton
          label={t("care.all_past_cycles.save_button")}
          onPress={handleSave}
          loading={isSavingCanonicalCycle}
          flex={1}
        />
      </DialogActions>
    </Dialog>
  );
}
